import * as THREE from 'three'
import { useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF, Outlines, Float } from '@react-three/drei'
import { RaycastPlane } from './RaycastPlane'
import { preload_assets } from '/data/PortfolioData'

export function TopCirclePaddles({
    topPaddleContainerRef,
    countPaddles,
    topCircleOffsetY,
    paddleMaterial,
    highlightColor,
    outlineColor,
    collisionPoint,
    setCollisionPoint,
    ignorePaddlesMouseReaction
}) {
    // console.log("recreating TopCirclePaddles. countPaddles: ", countPaddles)

    const topPaddleGLTF = useGLTF(preload_assets.glbUrls.top_paddle)
    const highlightColor2 = new THREE.Color(highlightColor)

    const [topPaddleContainerPosition, topPaddleContainerRotation] = useMemo(() => {
        const topPaddleContainerPosition = new THREE.Vector3(0, topCircleOffsetY, 0)
        const topPaddleContainerRotation = new THREE.Euler(Math.PI * 0.5, 0, 0)
        return [topPaddleContainerPosition, topPaddleContainerRotation]
    }, [topCircleOffsetY])

    const [topPaddles, paddleMaterials] = useMemo(() => {
        const topPaddles = []
        const paddleMaterials = []
        for (var i = 0; i < countPaddles; i++) {
            const paddle = topPaddleGLTF.scene.children[0].clone()
            paddle.scale.set(0.2, 0.2, 0.2)
            paddle.rotation.z = Math.PI * 0.2
            paddle.rotation.y = (Math.PI * 2 / countPaddles) * i
            // Create a new material for each paddle
            const newMaterial = paddleMaterial.clone()
            paddle.material = newMaterial
            paddleMaterials.push(newMaterial)
            topPaddles.push(paddle)
        }
        return [topPaddles, paddleMaterials]
    }, [countPaddles, topCircleOffsetY, paddleMaterial])

    const closestPaddleIndex = useRef(-1)

    useFrame(() => {
        if (!collisionPoint || topPaddles.length === 0) {
            // console.log("no collision point or no paddles")
            return
        }

        let targetScale = 0.2
        // console.log("topPaddleContainerRef parent rotation: ", topPaddleContainerRef.current.rotation.x, "ignorePaddlesMouseReaction: ", ignorePaddlesMouseReaction)
        if (ignorePaddlesMouseReaction) {
            // console.log("currentScale: ", currentScale, "resetting scale")
            topPaddles.forEach((paddle, index) => {
                paddle.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.1)
                // Reset color to default when not affected
                paddleMaterials[index].color.lerp(paddleMaterial.color, 0.1)
            })
            return
        }

        const distance = Math.sqrt(collisionPoint.x * collisionPoint.x + collisionPoint.y * collisionPoint.y)
        const angle = Math.atan2(collisionPoint.x, collisionPoint.y) - Math.PI * 1.0
        // Convert negative angles to positive by adding 2π
        const positiveAngle = angle < 0 ? angle + Math.PI * 2 : angle

        // we need to find the index of the paddle that is closest to the angle
        // TODO: tuve un error que daba 32 esta formula.. creo q tengo q restarle 1 al countPaddles pero no lo he confirmado
        closestPaddleIndex.current = Math.round(positiveAngle / (Math.PI * 2 / (countPaddles - 1)))
        // console.log("collisionPoint: ", collisionPoint, ", angle: ", positiveAngle, ", closestPaddleIndex: ", closestPaddleIndex.current)

        // Scale paddles based on distance
        const affectingPaddles = 5
        const maxScale = 0.25
        const minScale = 0.2
        const distanceScaleFactor = 0.015
        // let affectedPaddlesString = ""
        topPaddles.forEach((paddle, index) => {
            if (distance > 0.55 && distance < 2) {
                let currentPaddleIndex = index
                if (closestPaddleIndex.current > topPaddles.length - affectingPaddles && index < affectingPaddles) {
                    currentPaddleIndex += topPaddles.length
                }
                if (closestPaddleIndex.current < affectingPaddles && index > topPaddles.length - affectingPaddles) {
                    currentPaddleIndex -= topPaddles.length
                }
                const scaleFactor = maxScale - (Math.abs(currentPaddleIndex - closestPaddleIndex.current) * distanceScaleFactor)
                targetScale =
                    Math.abs(currentPaddleIndex - closestPaddleIndex.current) <= affectingPaddles ?
                        scaleFactor :
                        minScale
                targetScale = Math.max(targetScale, minScale)
            }
            paddle.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.1)

            // Interpolate color based on scale
            const scaleRatio = (targetScale - minScale) / (maxScale - minScale)
            if (scaleRatio > 0) {
                paddleMaterials[index].color.lerp(highlightColor2, scaleRatio * 0.3)
            } else {
                // Reset color to original when not affected
                paddleMaterials[index].color.lerp(paddleMaterial.color, 0.3)
            }
        })
        // console.log("affectedPaddlesString: ", affectedPaddlesString)
    })

    return (
        <>
            <Float
                speed={2}
                floatIntensity={0.2}
            >
                <group
                    ref={topPaddleContainerRef}
                    name="top-paddle-container"
                    position={topPaddleContainerPosition}
                    rotation={topPaddleContainerRotation}
                >
                    {topPaddles.map((paddle, index) => {
                        return <primitive
                            key={"top-paddle-" + index}
                            name={"top-paddle-" + index}
                            object={paddle}
                        >
                            <Outlines
                                thickness={1.5}
                                color={outlineColor}
                            />
                        </primitive>
                    })}
                </group>
            </Float>

            <RaycastPlane
                position={[0, 0, -0]} // Position it in front of the camera
                rotation={[0, 0, 0]} // No rotation needed
                size={[5.5, 4.0]} // Make it large enough to catch mouse events
                setCollisionPoint={setCollisionPoint}
            />
        </>
    )
} 